<template>
  <!-- Modal: Call Recording Details -->
  <!-- The dialog is controlled by the internal callRecordingDetailsDialog state -->
  <v-dialog v-model="callRecordingDetailsDialog" max-width="1000" persistent scrollable>
    <v-card v-if="selectedRecording"> <!-- Only render card content if selectedRecording is available -->
      <v-card-title class="grey lighten-3 py-2">
        Call Recording Details ({{ selectedRecording.callId }})
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog"> <!-- Use internal closeDialog method -->
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Context if found via direct search -->
      <v-alert
        v-if="selectedRecording.foundViaDirectSearch"
        type="info"
        dense
        text
        class="mx-4 mt-3 mb-0"
      >
        This call (Date: {{ selectedRecording.date }}) was found via direct ID search. You were previously viewing data for {{ selectedRecording.originalSearchDate | humanDate }}.
      </v-alert>

      <v-card-text class="pt-4">
        <audio v-if="audioUrl" :src="audioUrl" controls class="width-100"></audio>
        <v-sheet v-else color="grey lighten-4 pa-4 mb-4" rounded>
          The audio file is not available.
        </v-sheet>

        <div class="d-flex flex-column flex-sm-row">
          <div class="flex-1 mb-4 mb-sm-0">
            <div class="text-overline mb-2">Call Details</div>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td style="width: 80px;"><strong>ID</strong></td>
                    <td>{{ selectedRecording.callId }}</td>
                  </tr>
                  <tr>
                    <td><strong>Main Category</strong></td>
                    <td>{{ selectedRecording.categorization?.main || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Sub Category</strong></td>
                    <td>{{ selectedRecording.categorization?.sub || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Customer Effort Score</strong></td>
                    <td>{{ selectedRecording?.customer_effort?.score || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Agent</strong></td>
                    <td>{{ selectedRecording.agent || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Date</strong></td>
                    <td>{{ selectedRecording.date || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Time</strong></td>
                    <td>{{ selectedRecording.time || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Duration</strong></td>
                    <td>{{ selectedRecording.duration | secondsToMinutes }}</td>
                  </tr>
                  <tr>
                    <td><strong>Phone Number</strong></td>
                    <td>{{ selectedRecording.phoneNumber || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Language</strong></td>
                    <td>{{ selectedRecording.language || 'english' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Hold Time (est.)</strong></td>
                    <td>{{ selectedRecording.heuristic_hold_time !== undefined && selectedRecording.heuristic_hold_time !== null ? selectedRecording.heuristic_hold_time + 's' : 'N/A'}}</td> <!-- Improved null/undefined check -->
                  </tr>
                  <tr>
                    <td><strong>Sentiment</strong></td>
                    <td>{{ selectedRecording.sentiment_analysis?.overall_sentiment_score | sentimentScore }}</td>
                  </tr>
                  <tr>
                    <td><strong>Trajectory</strong></td>
                    <td>{{ selectedRecording.sentiment_analysis?.trajectory || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Call Number</strong></td>
                    <td>{{ selectedRecording.phone_number || 'N/A' }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <v-divider vertical class="mx-4 d-none d-sm-block"></v-divider>
          <v-divider class="my-4 d-sm-none"></v-divider>

          <div class="flex-2">
            <v-sheet color="grey lighten-4" rounded class="pa-4">
              <div class="d-flex flex-row mb-1 align-center">
                <strong>Summary</strong>
                <v-spacer></v-spacer>
                <v-btn icon small :color="detail == 1 ? 'blue' : 'primary'" @click="detail = 1"><v-icon small>mdi-tally-mark-1</v-icon></v-btn>
                <v-btn icon small :color="detail == 2 ? 'blue' : 'primary'" @click="detail = 2"><v-icon small>mdi-tally-mark-2</v-icon></v-btn>
                <v-btn icon small :color="detail == 3 ? 'blue' : 'primary'" @click="detail = 3"><v-icon small>mdi-tally-mark-3</v-icon></v-btn>
              </div>
              <div v-if="detail == 1" class="text-left">{{ selectedRecording.summary.brief }}</div>
              <div v-if="detail == 2" class="text-left">{{ selectedRecording.summary.short }}</div>
              <div v-if="detail == 3" class="text-left">{{ selectedRecording.summary.detailed }}</div>
            </v-sheet>
            <div class="text-overline my-2 d-flex justify-space-between align-center">
              <span>Conversation</span>
              <v-tooltip bottom v-if="selectedRecording.conversation && selectedRecording.conversation.length > 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small @click="conversationalStyle = !conversationalStyle" v-bind="attrs" v-on="on">
                    <v-icon>{{ conversationalStyle ? 'mdi-text-long' : 'mdi-forum-outline' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ conversationalStyle ? 'Show as raw text' : 'Show as bubbles' }}</span>
              </v-tooltip>
               <span v-else-if="selectedRecording.transcriptions && selectedRecording.transcriptions.original" class="text-caption text--secondary">(Raw text only)</span> <!-- Indicate if only raw text is available -->
            </div>

            <!-- Conversation/Transcription Display -->
            <div v-if="selectedRecording.conversation && selectedRecording.conversation.length > 0" style="max-height: 100%; overflow-y: auto;">
              <!-- Bubble style -->
              <div v-if="conversationalStyle" class="pr-2">
                <div v-for="(dialog, index) in selectedRecording.conversation" :key="index">
                  <div :class="['d-flex', dialog.startsWith('Agent:') ? 'justify-end' : 'justify-start']">
                    <div :class="['bubble', dialog.startsWith('Agent:') ? 'agent' : 'customer']" v-html="highlightText(dialog)"></div>
                  </div>
                </div>
              </div>
              <!-- Raw text style -->
              <div v-else>
                <pre style="white-space: pre-wrap; word-wrap: break-word; font-size: 0.9em;" v-html="highlightText(selectedRecording.transcriptions?.original || '')"></pre> <!-- Use ?. and default to '' -->
              </div>
            </div>
            <!-- Fallback to raw transcription if conversation array is empty/missing -->
            <div v-else-if="selectedRecording.transcriptions?.original" class="mt-4 text--secondary">
               <div class="text-overline mb-2">Transcription</div> <!-- Add a title -->
               <pre style="white-space: pre-wrap; word-wrap: break-word; font-size: 0.9em;" v-html="highlightText(selectedRecording.transcriptions.original)"></pre>
            </div>
             <div v-else class="mt-4 text--secondary">
                No conversation or transcription available.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">Close</v-btn> <!-- Use internal closeDialog method -->
      </v-card-actions>
    </v-card>
    <v-card v-else>
         <!-- Optional: Show a loading state or a "not found" message -->
        <v-card-title class="grey lighten-3 py-2">
             Loading Call Details...
             <v-spacer></v-spacer>
              <v-btn icon @click="closeDialog">
                <v-icon>mdi-close</v-icon>
             </v-btn>
        </v-card-title>
         <v-card-text class="text-center py-5">
            <v-progress-circular
                v-if="callId && !selectedRecording"
                indeterminate
                color="primary"
            ></v-progress-circular>
            <div v-else-if="!callId">Waiting for Call ID...</div>
            <div v-else>Call details not found for ID: {{ callId }}</div>
        </v-card-text>
         <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">Close</v-btn>
        </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getFirestore, collection, where, getDoc, getDocs, query, doc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { DateTime } from 'luxon';

export default {
  name: 'CallAnalysisCallDialog',
  props: {
    // The callId prop now controls whether the dialog should attempt to open.
    // It is *not* required, allowing the parent to pass null/undefined to hide it.
    callId: {
      type: String,
      // removed: required: true
    },
  },
  data() {
    return {
      // This internal state controls the v-dialog's open/closed state.
      // It is managed by the callId watcher and the closeDialog method.
      callRecordingDetailsDialog: false,
      selectedRecording: null, // Holds the fetched call data
      audioUrl: null, // Holds the audio download URL
      conversationalStyle: true, // Controls conversation display format
      detail: 1,
    }
  },
  // Remove the mounted hook as visibility is now watcher-driven
  // mounted() {
  //   // This is no longer needed. The watcher handles initial load if prop is present.
  //   // this.callRecordingDetailsDialog = true;
  // },
  methods: {
    async fetchCallRecording() {
      console.log("fetchCallRecording triggered for ID:", this.callId);

      // Clear previous state immediately and hide dialog while fetching new data
      this.selectedRecording = null;
      this.audioUrl = null;
      // Don't set dialog to false here, the watcher handles the transition based on prop value.
      // If callId is null/empty, the watcher calls closeDialog.
      // If callId is valid, we hide the content with v-if="selectedRecording" until data loads.

      // --- Validation ---
      // If callId is null, undefined, or empty, consider the dialog "closed" from a data perspective.
      if (!this.callId || typeof this.callId !== 'string' || this.callId.trim() === '') {
        console.log("Invalid or empty callId received. Ensuring dialog is closed.");
        this.closeDialog(); // Use the closeDialog method to clear state and hide
        return; // Stop the fetch process
      }
      // --- End Validation ---


      const db = getFirestore();
      const callRef = doc(collection(db, 'cc_callanalysis_calls'), this.callId);

      try {
        const callDoc = await getDoc(callRef);
        console.log(`Document ${this.callId} exists:`, callDoc.exists());

        if (callDoc.exists()) {
           const data = callDoc.data();
           let audioLoaded = false; // Flag to track audio fetch success

           // Attempt to get audio URL
           try {
             const path = `customer_service/call_analysis/recordings/mp3/${this.callId}.mp3`;
             const storage = getStorage();
             const storageRef = ref(storage, path);
             const url = await getDownloadURL(storageRef);
             this.audioUrl = url;
             audioLoaded = true;
             console.log("Audio URL fetched successfully.");
           } catch (storageError) {
              console.warn("Error fetching audio URL:", storageError);
              this.audioUrl = null; // Explicitly null if audio fails
              audioLoaded = false;
              // Continue even if audio fails, maybe the user wants to see transcript/details
           }

           // Set the main data object and open the dialog
           this.selectedRecording = data;
           this.selectedRecording.callId = this.callId; // Ensure callId is on the object
           this.callRecordingDetailsDialog = true; // <--- Open dialog AFTER main data (and optionally audio) is loaded
           console.log("Call data loaded. Dialog should now be visible.");

        } else {
          // Document not found for the provided ID
          console.warn(`Call recording document not found for ID: ${this.callId}.`);
          // Clear state and hide the dialog if the ID didn't match a document
          this.closeDialog();
          // Optional: You could add a simple internal message state here to show "Not Found" inside the dialog
          // before closeDialog clears everything.
        }
      } catch (dbError) {
          console.error("Error fetching call document:", dbError);
          // Clear state and hide dialog on database error
          this.closeDialog();
          // Optional: Add logic to show a user-friendly error message
      }
    },
    closeDialog() {
       // This method is called when the user explicitly closes the dialog
       // or when the component determines it should hide (e.g., invalid callId).
       console.log("Closing dialog and clearing state.");
       this.callRecordingDetailsDialog = false;
       // Clear state when closed
       this.selectedRecording = null;
       this.audioUrl = null;
       // We do *not* emit an event here as per requirement.
    },
    highlightText(text) {
      // Add robustness: Ensure text is a string and not null/undefined
      if (typeof text !== 'string' || !text) {
        return text; // Return original value (e.g., null, undefined, empty string)
      }
      // Basic example: wrap words in a span for highlighting style
      return text.replace(/(\b\w+\b)/g, '<span class="highlight">$1</span>');
    }
  },
  watch: {
    // Watch for changes in the callId prop provided by the parent.
    // This is the primary trigger for showing/hiding the dialog and fetching data.
    callId: {
      handler(newId, oldId) {
        console.log(`callId prop changed: ${oldId} -> ${newId}`);
        // If the newId is a valid string, trigger the fetch process.
        // fetchCallRecording will handle setting callRecordingDetailsDialog = true upon success.
        // If newId is null/undefined/empty, fetchCallRecording will call closeDialog.
        this.fetchCallRecording();
      },
      immediate: true, // Run this handler once immediately when the component mounts.
                      // If callId is initially provided, it fetches. If not, it runs the validation
                      // in fetchCallRecording and keeps the dialog closed.
    },
    // Watch the internal state that is bound to v-model.
    // This detects when the user closes the dialog via means *other* than the close buttons
    // (like clicking the overlay or pressing Escape).
    callRecordingDetailsDialog(newValue, oldValue) {
        console.log(`callRecordingDetailsDialog changed: ${oldValue} -> ${newValue}`);
        if (!newValue && oldValue === true) {
            // The dialog was open and is now closed.
            // This means the user likely closed it via means other than our close buttons.
            // Ensure internal state is cleared in this case as well.
             if (this.selectedRecording !== null) { // Only clear if there was data loaded
                console.log("Dialog was open and is now closed (likely user action). Clearing internal state.");
                this.selectedRecording = null;
                this.audioUrl = null;
                // No emit needed
             }
        }
        // Note: When callId becomes null/empty, the callId watcher calls closeDialog,
        // which sets callRecordingDetailsDialog = false. This watcher *will* run
        // due to that change, but the `if (!newValue && oldValue === true)` condition
        // will be met, and the clearing logic inside is redundant with closeDialog's
        // clearing logic. This is fine; it ensures state is clear regardless of how it closes.
    }
  },
  computed: {
    // Keep computed properties, adding null/undefined checks for robustness
    formattedDate() {
      // Check if selectedRecording and date exist before formatting
      return this.selectedRecording?.date ? DateTime.fromISO(this.selectedRecording.date).toFormat('yyyy-MM-dd') : 'N/A';
    }
  },
  filters: {
    // Add null/undefined checks to filters
    humanDate(date) {
      if (!date) return 'N/A';
      try {
         return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
      } catch (e) {
         console.error("Error formatting date:", date, e);
         return 'Invalid Date';
      }
    },
    secondsToMinutes(seconds) {
      if (seconds == null || isNaN(seconds) || seconds < 0) {
        return '--:--'; // Indicate missing or invalid data
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      const paddedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;
      return `${minutes}:${paddedSeconds}`;
    },
    sentimentScore(score) {
      if (score == null || isNaN(score)) {
        return 'N/A'; // Indicate missing or invalid data
      }
      return score.toFixed(2);
    }
  }
}
</script>

<style scoped>
/* Keep your styles */
.highlight {
  background-color: #f0f0f0;
  padding: 2px 4px;
}
.bubble {
  padding: 8px 15px; /* Slightly smaller padding */
  border-radius: 15px; /* More rounded */
  max-width: 80%; /* Allow slightly wider bubbles */
  color: white;
  text-align: left;
  margin-bottom: 10px; /* Smaller margin */
  line-height: 1.4;
  word-wrap: break-word; /* Ensure long words break */
}
.customer.bubble {
  background-color: #1976D2; /* Vuetify primary blue */
  border-bottom-right-radius: 5px; /* Flat corner */
}
.agent.bubble {
  background-color: #546E7A; /* Vuetify blue-grey */
  color: #fff;
  border-bottom-left-radius: 5px; /* Flat corner */
}

/* Ensure full width for audio player */
.width-100 {
  width: 100%;
}
</style>